import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, LogOut, LogIn, ChevronLeft, Menu, X, Home, BookOpen, LayoutDashboard, PlusSquare } from 'lucide-react';
import logo from '../crialogo.png';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, isAdmin } = useAuth();

  const isActive = (path) => location.pathname === path;

  const isLearningMode =
    location.pathname.startsWith('/learn/') ||
    location.pathname.startsWith('/quiz/');

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  const handleNavClick = () => setMenuOpen(false);

  if (isLearningMode) {
    return (
      <header className="learning-header mobile-header">
        <div className="header-container">
          <div className="header-section left">
            <button
              className="icon-btn"
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <ChevronLeft size={22} />
            </button>
          </div>
          <div className="header-section center">
            <img src={logo} alt="Logo" className="logo" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
            <span className="title">The Reef</span>
          </div>
          <div className="header-section right">
            <div className="mobile-avatar" title={currentUser?.displayName || 'User'}>
              {getInitials(currentUser?.displayName)}
            </div>
            <button
              className="icon-btn"
              onClick={() => logout()}
              title="Logout"
              aria-label="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Desktop Header */}
      <header className="desktop-header">
        <div className="container">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', gap: '32px', width: '100%' }}>
            {/* Logo */}
            <Link to="/" className="logo" style={{ flexShrink: 0 }}>
              <img
                src={logo}
                alt="Coral Reef Innovation"
                style={{ width: '36px', height: 'auto' }}
              />
              <span className="logo-main">The Reef</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="desktop-nav" style={{ flex: 1 }}>
              <ul style={{ gap: '24px' }}>
                <li><Link to="/" className={isActive('/') ? 'active' : ''}>Home</Link></li>
                <li><Link to="/courses" className={isActive('/courses') ? 'active' : ''}>Courses</Link></li>
                {currentUser && (
                  <li><Link to="/dashboard" className={isActive('/dashboard') ? 'active' : ''}>My Learning</Link></li>
                )}
                {isAdmin && (
                  <li><Link to="/admin/upload" className={isActive('/admin/upload') ? 'active' : ''}>Create Course</Link></li>
                )}
              </ul>
            </nav>

            {/* Desktop Actions */}
            <div className="header-actions desktop-actions" style={{ flexShrink: 0, gap: '12px' }}>
              <div className="search-bar" style={{ width: '260px' }}>
                <Search size={16} className="icon" />
                <input
                  type="text"
                  placeholder="What do you want to learn?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="user-actions" style={{ gap: '4px' }}>
                {currentUser ? (
                  <>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', padding: '0 8px', whiteSpace: 'nowrap' }}>
                      {currentUser.displayName?.split(' ')[0] || 'User'}
                    </span>
                    <button className="icon-btn" title="Notifications">
                      <Bell size={18} />
                    </button>
                    <button className="icon-btn" onClick={logout} title="Log out">
                      <LogOut size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="btn btn-secondary" style={{ padding: '8px 18px' }}>
                      Log In
                    </Link>
                    <Link to="/signup" className="btn btn-primary" style={{ padding: '8px 18px' }}>
                      Join Free
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', width: '100%' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
            <img src={logo} alt="Logo" style={{ width: '30px', height: '30px', borderRadius: '6px' }} />
            <span className="title">The Reef</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {currentUser && (
              <button className="icon-btn" aria-label="Notifications">
                <Bell size={20} />
              </button>
            )}
            <button
              className="icon-btn hamburger-btn"
              style={{ display: 'flex' }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav Drawer */}
      <div className={`mobile-nav-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-inner">
          {/* User Info */}
          {currentUser && (
            <div className="mobile-user-info">
              <div className="mobile-avatar-lg">
                {getInitials(currentUser.displayName)}
              </div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--primary)' }}>
                  {currentUser.displayName || 'User'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                  {currentUser.email}
                </div>
              </div>
            </div>
          )}

          {/* Search bar in mobile nav */}
          <div className="mobile-search-bar">
            <Search size={16} className="icon" style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Nav Links */}
          <nav className="mobile-nav-links">
            <Link to="/" className={`mobile-nav-item ${isActive('/') ? 'active' : ''}`} onClick={handleNavClick}>
              <Home size={18} /> Home
            </Link>
            <Link to="/courses" className={`mobile-nav-item ${isActive('/courses') ? 'active' : ''}`} onClick={handleNavClick}>
              <BookOpen size={18} /> Courses
            </Link>
            {currentUser && (
              <Link to="/dashboard" className={`mobile-nav-item ${isActive('/dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                <LayoutDashboard size={18} /> My Learning
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin/upload" className={`mobile-nav-item ${isActive('/admin/upload') ? 'active' : ''}`} onClick={handleNavClick}>
                <PlusSquare size={18} /> Create Course
              </Link>
            )}
          </nav>

          {/* Login / Logout */}
          <div className="mobile-nav-footer">
            {currentUser ? (
              <button className="btn btn-secondary mobile-logout-btn" onClick={handleLogout}>
                <LogOut size={16} /> Sign Out
              </button>
            ) : (
              <Link to="/login" className="btn btn-primary" onClick={handleNavClick} style={{ width: '100%', justifyContent: 'center' }}>
                <LogIn size={16} /> Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {menuOpen && <div className="mobile-nav-backdrop" onClick={() => setMenuOpen(false)} />}
    </>
  );
};

export default Header;
